import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  CardRow,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
} from '../../../components/ui';
import { Button } from '../../../components/button';
import { Modal } from '../../../components/modal';
import { CardSetupForm } from '../../../components/CardSetupForm';
import { getBrand } from '../../../constants/brands';
import { toastActions } from '../../../../store/toastStore';
import {
  useConfirmSavedCardMutation,
  useCreateSetupIntentForSavedCardMutation,
  useMySavedPaymentMethodsQuery,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
} from '../../../services/stripeService/__generated__/queries.generated';
import { cardExpiry, cardTitle } from '../../../utils/cardLabels';
import {
  removalMessage,
  removalPrompt,
  SavedCardRow,
  savedCardMessage,
  sortSavedCards,
} from './paymentMethods';

const CardText = styled.div`
  ${tw`flex flex-col gap-1 min-w-0`}
`;

const Badges = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const RowActions = styled.div`
  ${tw`flex flex-wrap gap-2 shrink-0`}
`;

const ModalBody = styled.div`
  ${tw`w-full`}
`;

const ModalTitle = styled.h2`
  ${tw`text-lg font-semibold mb-1`}
  color: var(--color-text-primary);
`;

const ModalLead = styled.p`
  ${tw`text-sm mb-3`}
  color: var(--color-text-secondary);
`;

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong. Please try again.';

/**
 * Spec 013 P4 (FR-015): the guest's saved cards on this site. Cards live in Stripe on this
 * brand's account; another brand never sees them.
 */
export default function PaymentMethodsPage() {
  const brandName = getBrand().name;
  const { data, loading, error, refetch } = useMySavedPaymentMethodsQuery({
    fetchPolicy: 'network-only',
  });
  const [createSetupIntent] = useCreateSetupIntentForSavedCardMutation();
  const [confirmSavedCard] = useConfirmSavedCardMutation();
  const [setDefault] = useSetDefaultPaymentMethodMutation();
  const [removeCard] = useRemovePaymentMethodMutation();
  const [adding, setAdding] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [busyCardId, setBusyCardId] = useState<string | null>(null);

  const cards = sortSavedCards((data?.mySavedPaymentMethods ?? []) as SavedCardRow[]);

  const loadSetup = useCallback(async () => {
    const { data: answer } = await createSetupIntent();
    const setup = answer?.createSetupIntentForSavedCard;
    if (!setup?.clientSecret) throw new Error('The card form could not be opened.');
    return { clientSecret: setup.clientSecret };
  }, [createSetupIntent]);

  const onCardReady = async ({ setupIntentId }: { setupIntentId: string }) => {
    try {
      const { data: answer } = await confirmSavedCard({ variables: { setupIntentId } });
      toast.success(savedCardMessage(Boolean(answer?.confirmSavedCard.duplicate)));
      setAdding(false);
      await refetch();
    } catch (err) {
      // A refused card was detached and its SetupIntent is spent: start a fresh form.
      toast.error(errorMessage(err));
      setFormKey(key => key + 1);
    }
  };

  const makeDefault = async (card: SavedCardRow) => {
    setBusyCardId(card.id);
    try {
      await setDefault({ variables: { paymentMethodId: card.id } });
      toast.success(`${cardTitle(card)} is now your default card.`);
      await refetch();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyCardId(null);
    }
  };

  const remove = (card: SavedCardRow) => {
    toastActions.showToastAlert(
      `Remove ${cardTitle(card)}?`,
      removalPrompt(card),
      '',
      async () => {
        setBusyCardId(card.id);
        try {
          const { data: answer } = await removeCard({ variables: { paymentMethodId: card.id } });
          if (answer) toast.success(removalMessage(answer.removePaymentMethod));
          await refetch();
        } catch (err) {
          toast.error(errorMessage(err));
        } finally {
          setBusyCardId(null);
        }
      },
      () => undefined,
      'Remove',
    );
  };

  return (
    <PageContainer width="md" canvas>
      <PageHeader
        eyebrow="Account"
        title="Payment methods"
        description={`Cards you saved on ${brandName}. They are only used on this site.`}
        backTo={{ to: '/profile', label: 'Account' }}
      />
      <Stack>
        <Card>
          <CardHeader
            title="Saved cards"
            description="Your default card is selected first at checkout."
            actions={
              <Button
                text="Add card"
                size="sm"
                onClick={() => {
                  setFormKey(key => key + 1);
                  setAdding(true);
                }}
              />
            }
          />
          {loading && !data ? (
            <LoadingBlock />
          ) : error ? (
            <CardRow>
              <Alert tone="error">{errorMessage(error)}</Alert>
            </CardRow>
          ) : cards.length === 0 ? (
            <EmptyState
              compact
              title="No saved cards"
              description="Add a card here, or tick “Save” when you pay for a trip."
            />
          ) : (
            cards.map(card => (
              <CardRow key={card.id} data-testid="saved-card-row">
                <CardText>
                  <Strong>{cardTitle(card)}</Strong>
                  <Muted $size="sm">{cardExpiry(card)}</Muted>
                  <Badges>
                    {card.isDefault ? <Badge tone="brand">Default</Badge> : null}
                    {card.isExpired ? <Badge tone="error">Expired</Badge> : null}
                    {card.inUseByBookingIds.length > 0 ? (
                      <Badge tone="info">Used for a trip</Badge>
                    ) : null}
                  </Badges>
                </CardText>
                <RowActions>
                  {!card.isDefault && !card.isExpired ? (
                    <Button
                      theme="secondary"
                      size="sm"
                      text="Make default"
                      isDisabled={busyCardId !== null}
                      isLoading={busyCardId === card.id}
                      onClick={() => makeDefault(card)}
                    />
                  ) : null}
                  <Button
                    theme="text"
                    size="sm"
                    text="Remove"
                    isDisabled={busyCardId !== null}
                    onClick={() => remove(card)}
                  />
                </RowActions>
              </CardRow>
            ))
          )}
        </Card>
        <Alert tone="neutral">
          Card numbers are kept by our payment processor, Stripe, not by us. A card used for a trip
          stays on file for that trip&apos;s charges until it is settled, even after you remove it
          here.
        </Alert>
      </Stack>

      <Modal
        isOpen={adding}
        onClose={() => setAdding(false)}
        modalWidth="min(480px, calc(100vw - 2rem))"
        modalHeight="auto"
        title="Add a card"
      >
        <ModalBody>
          <ModalTitle>Add a card</ModalTitle>
          <ModalLead>The card must be in your own name.</ModalLead>
          {adding ? (
            <CardSetupForm
              key={formKey}
              loadSetup={loadSetup}
              onCardReady={onCardReady}
              submitLabel="Save card"
            />
          ) : null}
        </ModalBody>
      </Modal>
    </PageContainer>
  );
}
